import { useMarketplaceItems } from '@/apis/marketplace/hooks/useMarketplaceItems'
import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import { useApplicantProgramYears } from '@/app/SchoolYearProvider'
import { usePagination } from '@/common/hooks/usePagination.tsx'
import {
  useMarketplaceStores,
  useMarketplaceAffiliations,
} from '@/queries/marketplace/hooks.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectProgramSettings } from '@/storage/program_settings/programSettingsSlice'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import MarketplaceUI from '../components/MarketplaceUI'
import { MarketplaceAffiliation } from '@/queries/marketplace/schema'
import { TABLE_FILTERS } from '@/app/pages/marketplace/constants'

type MarketplaceView = 'stores' | 'items' | 'affiliations'

export default function MarketplacePage() {
  const navigate = useNavigate()

  const [showCart, setShowCart] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<string>('')
  const [currentView, setCurrentView] = useState<MarketplaceView>('stores')

  const debouncedValue = useDebounce(search, 500)
  const { selectedProgramId } = useApplicantProgramYears()
  const { currentPage, perPage, setCurrentPage, setPerPage } = usePagination()

  const { data: storesData } = useMarketplaceStores(
    selectedProgramId,
    selectedFilter,
    debouncedValue,
    perPage,
    currentPage,
    {
      enabled: currentView === 'stores' && selectedProgramId != null,
    }
  )

  const { data: affiliationsData } = useMarketplaceAffiliations(
    selectedProgramId,
    debouncedValue,
    perPage,
    currentPage,
    {
      enabled: currentView === 'affiliations' && selectedProgramId != null,
    }
  )

  const { data: itemsRecords } = useMarketplaceItems(
    selectedProgramId,
    selectedFilter,
    debouncedValue,
    undefined,
    undefined,
    perPage,
    currentPage,
    {
      enabled: currentView === 'items' && selectedProgramId != null,
    }
  )

  // Computed values
  const stores = storesData?.data ?? []
  const affiliations = affiliationsData?.data ?? []
  const items = itemsRecords?.data ?? []

  const totalRecords =
    currentView === 'stores'
      ? (storesData?.total ?? 0)
      : currentView === 'affiliations'
        ? (affiliationsData?.total ?? 0)
        : (itemsRecords?.total ?? 0)

  const programSettings = useSelector(selectProgramSettings)
  const pathname = useLocation().pathname

  useEffect(() => {
    if (programSettings.marketplace_module == 0) navigate(-1)
    else if (pathname.includes(AppRoutePaths.marketplace.cart.index))
      setShowCart(true)
  }, [])

  const handleShopClick = (item: MarketplaceItem) => {
    navigate(
      AppRoutePaths.marketplace.details.index.replace(
        ':itemId',
        item.id.toString()
      )
    )
  }

  const hideCart = () => {
    navigate(AppRoutePaths.marketplace.index)
    setShowCart(false)
  }

  const handleFilterOnChange = (item: { id: number; name: string }) => {
    setSelectedFilter(
      item.id == 0 ? '' : item.id == 1 ? '1,2' : item.id.toString()
    )
    setCurrentPage(1)
  }

  const handleViewChange = (view: MarketplaceView) => {
    setCurrentView(view)
    setSearch('')
    setSelectedFilter('')
    setCurrentPage(1)
  }

  const handleProviderClick = (providerId: number) => {
    navigate(
      AppRoutePaths.marketplace.provider.index.replace(
        ':providerId',
        providerId.toString()
      )
    )
  }

  function handleStoreClick(storeId: number) {
    navigate(
      AppRoutePaths.marketplace.provider.index.replace(
        ':providerId',
        storeId.toString()
      )
    )
  }

  const handleAffiliationClick = (affiliation: MarketplaceAffiliation) => {
    navigate(
      AppRoutePaths.marketplace.affiliation.index.replace(
        ':affiliationId',
        affiliation.id.toString()
      )
    )
  }

  return (
    <MarketplaceUI
      stores={stores}
      affiliations={affiliations}
      items={items}
      totalRecords={totalRecords}
      showCart={showCart}
      search={search}
      handleStoreClick={handleStoreClick}
      currentView={currentView}
      currentPage={currentPage}
      perPage={perPage}
      filters={TABLE_FILTERS}
      onSearchChange={setSearch}
      onViewChange={handleViewChange}
      onFilterChange={handleFilterOnChange}
      onItemClick={handleShopClick}
      onAffiliationClick={handleAffiliationClick}
      onProviderClick={handleProviderClick}
      onPageChange={setCurrentPage}
      onPerPageChange={setPerPage}
      hideCart={hideCart}
    />
  )
}
